import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import api from "../../services/api";
import AdminLogoHeader from "../../components/admin/AdminLogoHeader";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const ComplianceCRM = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const loadApprovedUsers = useCallback(async (term = "") => {
    try {
      setLoading(true);
      setError("");
      const params = term ? { q: term } : {};
      const data = await api.adminListApprovedUsers(params);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("approved users", err);
      setError(err.message || "Unable to load approved users.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApprovedUsers();
  }, [loadApprovedUsers]);

  const handleSubmit = (event) => {
    event.preventDefault();
    loadApprovedUsers(search.trim());
  };

  const handleReset = () => {
    setSearch("");
    loadApprovedUsers();
  };

  const totalCount = useMemo(() => rows.length, [rows.length]);

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminLogoHeader />
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <section className="bg-white rounded-2xl shadow-sm border border-slate-100">
          <div className="flex flex-col gap-4 px-6 py-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">CRM · Approved clients</h1>
              <p className="text-sm text-slate-500">
                Browse all clients who have completed compliance review. Use preview to drill into full records.
              </p>
            </div>
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
              <form onSubmit={handleSubmit} className="flex items-center gap-2">
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by name or contact"
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                />
                <button
                  type="submit"
                  className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                  disabled={loading}
                >
                  Search
                </button>
              </form>
              <button
                type="button"
                onClick={handleReset}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100"
                disabled={loading}
              >
                Reset
              </button>
            </div>
          </div>

          <div className="px-6 pb-4 text-sm text-slate-500">
            {loading ? "Loading approved users..." : `${totalCount} approved client${totalCount === 1 ? "" : "s"}`}
          </div>

          {error ? (
            <p className="px-6 pb-6 text-sm text-red-600">{error}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Client</th>
                    <th className="px-4 py-3 text-left font-medium">Contact</th>
                    <th className="px-4 py-3 text-left font-medium">Phone</th>
                    <th className="px-4 py-3 text-left font-medium">Role</th>
                    <th className="px-4 py-3 text-left font-medium">Created</th>
                    <th className="px-4 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                        Loading...
                      </td>
                    </tr>
                  ) : !rows.length ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                        No approved clients found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id} className="hover:bg-slate-50">
                        <td className="px-4 py-4">
                          <p className="font-medium text-slate-900">
                            {row.full_name || row.username || "Unnamed client"}
                          </p>
                          <p className="text-xs text-slate-500">
                            {row.is_verified ? "Verified" : "Unverified"} ·{" "}
                            {row.kyc_approved ? "KYC compliant" : "Pending KYC"}
                          </p>
                        </td>
                        <td className="px-4 py-4 text-slate-600">{row.username}</td>
                        <td className="px-4 py-4 text-slate-600">{row.phone_number || "-"}</td>
                        <td className="px-4 py-4 text-slate-600 capitalize">{row.role || "-"}</td>
                        <td className="px-4 py-4 text-slate-600">{formatDate(row.created_at)}</td>
                        <td className="px-4 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => navigate(`/admin/compliance/crm/${row.id}`)}
                            className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100"
                          >
                            Preview
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ComplianceCRM;
